import "./topbar.css";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, ChevronDown, X, LogOut, User, ArrowLeftRight, Menu } from "lucide-react";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { OLD_PORTAL_URL } from "../../utils/oldPortalUrl";
import {
  collection,
  doc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  getDocs,
  writeBatch,
} from "firebase/firestore";

// `onMenuClick` toggles the off-canvas sidebar; the trigger is CSS-hidden on
// desktop where the sidebar is always on screen.
export default function Topbar({ onMenuClick }) {
  const navigate = useNavigate();
  const { globalUser, globalData, logOut } = useAuth();
  const isClient = localStorage.getItem("userType") === "client";

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const notificationPanelRef = useRef(null);
  const profileRef = useRef(null);

  const displayName =
    globalData?.name || globalData?.displayName || globalUser?.email || "User";
  const roleLabel = isClient ? "Client" : "Lawyer";
  const initial = (displayName || "U").trim().charAt(0).toUpperCase();
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Real-time notifications (ported from the legacy Navbar so the CRM topbar
  // is fully functional across every portal page).
  useEffect(() => {
    if (!globalUser) return;
    const userCollection = isClient ? "client" : "lawyers";
    const userDocRef = doc(db, userCollection, globalUser.uid);
    const notificationsRef = collection(userDocRef, "notifications");
    const notificationsQuery = query(notificationsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        setNotifications(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => console.error("Error fetching notifications:", error)
    );
    return () => unsubscribe();
  }, [globalUser, isClient]);

  // Close popovers on outside click.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isNotificationOpen &&
        notificationPanelRef.current &&
        !notificationPanelRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
      if (
        isProfileOpen &&
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotificationOpen, isProfileOpen]);

  const deleteNotification = async (notificationId) => {
    if (!globalUser) return;
    try {
      const userCollection = isClient ? "client" : "lawyers";
      const userDocRef = doc(db, userCollection, globalUser.uid);
      const notifRef = doc(collection(userDocRef, "notifications"), notificationId);
      await deleteDoc(notifRef);
    } catch (error) {
      console.error(`Error deleting notification ${notificationId}:`, error);
    }
  };

  const deleteAllNotifications = async () => {
    if (!globalUser) return;
    try {
      const userCollection = isClient ? "client" : "lawyers";
      const userDocRef = doc(db, userCollection, globalUser.uid);
      const notificationsColRef = collection(userDocRef, "notifications");
      const snapshot = await getDocs(notificationsColRef);
      const batch = writeBatch(db);
      snapshot.docs.forEach((d) => batch.delete(d.ref));
      await batch.commit();
    } catch (error) {
      console.error("Error deleting all notifications:", error);
    }
  };

  const handleNotificationTap = (notification) => {
    const { notificationType, caseId, collection: col } = notification;
    if (notificationType === "app_update") {
      window.open(
        "https://play.google.com/store/apps/details?id=com.jurident.valsco&hl=en_IN",
        "_blank"
      );
      return;
    }
    if (
      notificationType === "case_shared" ||
      notificationType === "case_access_removed" ||
      notificationType === "event_reminder"
    ) {
      if (!caseId || !col) return;
      setIsNotificationOpen(false);
      navigate(`/portal/case/${caseId}`, { state: { status: col } });
    }
  };

  const handleLogout = () => {
    logOut();
    navigate("/portal/onboarding");
  };

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button
          type="button"
          className="menu-button"
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>

        <div className="welcome-section">
          <h2>Welcome back, {displayName}</h2>
          <p>Stay on top of your cases, tasks and deadlines.</p>
        </div>
      </div>

      <div className="topbar-right">
        <div className="search-container">
          <Search size={16} />
          <input type="search" placeholder="Search..." />
        </div>

        <button
          className="notification-button"
          onClick={() => setIsNotificationOpen(true)}
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unreadCount > 0 && <span className="notification-dot"></span>}
        </button>

        <div className="profile-section" ref={profileRef}>
          <button
            className="profile-trigger"
            onClick={() => setIsProfileOpen((v) => !v)}
          >
            <div className="profile-avatar">{initial}</div>
            <div className="profile-text">
              <h4>{displayName}</h4>
              <span>{roleLabel}</span>
            </div>
            <ChevronDown size={16} />
          </button>

          {isProfileOpen && (
            <div className="profile-dropdown">
              <button
                onClick={() => {
                  setIsProfileOpen(false);
                  navigate("/portal/account");
                }}
              >
                <User size={16} />
                Account
              </button>
              {/* Full-page nav to the /old proxy serving the original portal. */}
              <button
                onClick={() => {
                  window.location.href = OLD_PORTAL_URL;
                }}
              >
                <ArrowLeftRight size={16} />
                Switch to normal view
              </button>
              <button className="logout-item" onClick={handleLogout}>
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Notifications panel */}
      {isNotificationOpen && (
        <div className="notif-overlay">
          <div className="notif-panel" ref={notificationPanelRef}>
            <div className="notif-header">
              <h2>Notifications</h2>
              <button
                onClick={() => {
                  deleteAllNotifications();
                  setIsNotificationOpen(false);
                }}
                aria-label="Clear all notifications"
              >
                <X size={20} />
              </button>
            </div>
            {notifications.length > 0 ? (
              notifications.map((notif) => {
                const isCaseNotification =
                  notif.notificationType === "case_shared" ||
                  notif.notificationType === "case_access_removed" ||
                  notif.notificationType === "event_reminder" ||
                  notif.notificationType === "app_update";
                return (
                  <div
                    key={notif.id}
                    onClick={
                      isCaseNotification
                        ? () => handleNotificationTap(notif)
                        : undefined
                    }
                    className={`notif-item ${notif.read ? "" : "unread"} ${
                      isCaseNotification ? "tappable" : ""
                    }`}
                  >
                    <button
                      className="notif-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notif.id);
                      }}
                      aria-label="Delete notification"
                    >
                      <X size={16} />
                    </button>
                    <h3>{notif.title}</h3>
                    <p>{notif.body}</p>
                    <small>{notif.timestamp?.toDate().toLocaleString()}</small>
                  </div>
                );
              })
            ) : (
              <p className="notif-empty">No notifications available.</p>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
